/*
  Medella Springs sections (ms-*)
  Small custom elements used by the homepage sections:
  <ms-slider>, <ms-product-tabs>, <ms-timeline>.
*/

if (!customElements.get('ms-slider')) {
  customElements.define(
    'ms-slider',
    class MsSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-ms-track]');
        if (!this.track) return;

        // Buttons may live outside the element (e.g. in a section header), so look them up by id too.
        const scope = this.closest('.ms-section') || this;
        const prevs = scope.querySelectorAll(`[data-ms-prev="${this.id}"]`);
        const nexts = scope.querySelectorAll(`[data-ms-next="${this.id}"]`);
        this.prevButtons = [...this.querySelectorAll('[data-ms-prev=""]'), ...prevs];
        this.nextButtons = [...this.querySelectorAll('[data-ms-next=""]'), ...nexts];

        this.prevButtons.forEach((button) => button.addEventListener('click', () => this.step(-1)));
        this.nextButtons.forEach((button) => button.addEventListener('click', () => this.step(1)));

        this.onScroll = () => this.updateButtons();
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.updateButtons());
        this.resizeObserver.observe(this.track);
        this.updateButtons();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      visibleItems() {
        return [...this.track.children].filter((item) => !item.hidden && item.offsetParent !== null);
      }

      step(direction) {
        const items = this.visibleItems();
        if (!items.length) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const distance = items[0].getBoundingClientRect().width + gap;
        this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
      }

      scrollToItem(item) {
        if (!item) return;
        const offset = item.offsetLeft - this.track.offsetLeft;
        this.track.scrollTo({ left: offset, behavior: 'smooth' });
      }

      reset() {
        this.track.scrollTo({ left: 0 });
        this.updateButtons();
      }

      updateButtons() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
        const atStart = this.track.scrollLeft <= 2;
        const atEnd = this.track.scrollLeft >= maxScroll;
        this.prevButtons.forEach((button) => (button.disabled = atStart));
        this.nextButtons.forEach((button) => (button.disabled = atEnd || maxScroll <= 0));
      }
    }
  );
}

if (!customElements.get('ms-product-tabs')) {
  customElements.define(
    'ms-product-tabs',
    class MsProductTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-ms-tab]')];
        this.cards = [...this.querySelectorAll('[data-ms-tabs]')];
        this.slider = this.querySelector('ms-slider');
        this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));

        const initial = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
        if (initial) this.select(initial, false);
      }

      select(tab, scrollTab = true) {
        const key = tab.dataset.msTab;
        this.tabs.forEach((item) => item.setAttribute('aria-selected', item === tab ? 'true' : 'false'));
        this.cards.forEach((card) => {
          const keys = card.dataset.msTabs.split(' ');
          card.hidden = !keys.includes(key);
        });
        if (this.slider && this.slider.reset) this.slider.reset();
        if (scrollTab) tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }
  );
}

if (!customElements.get('ms-timeline')) {
  customElements.define(
    'ms-timeline',
    class MsTimeline extends HTMLElement {
      connectedCallback() {
        this.items = [...this.querySelectorAll('[data-ms-item]')];
        this.images = [...this.querySelectorAll('[data-ms-image]')];
        this.interval = parseInt(this.dataset.interval, 10) || 0;
        this.index = 0;

        this.items.forEach((item, index) => {
          const button = item.querySelector('button');
          (button || item).addEventListener('click', () => {
            this.activate(index);
            this.restart();
          });
        });

        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.restart());
        this.activate(0);
        this.restart();
      }

      disconnectedCallback() {
        this.stop();
      }

      activate(index) {
        this.index = index;
        this.items.forEach((item, i) => {
          item.classList.toggle('is-active', i === index);
          const button = item.querySelector('button');
          if (button) button.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
        this.images.forEach((image) => image.classList.toggle('is-active', Number(image.dataset.msImage) === index));
      }

      restart() {
        this.stop();
        if (this.interval > 0 && this.items.length > 1 && !(window.Shopify && window.Shopify.designMode)) {
          this.timer = setInterval(() => this.activate((this.index + 1) % this.items.length), this.interval * 1000);
        }
      }

      stop() {
        clearInterval(this.timer);
      }
    }
  );
}

// Theme editor: reveal the selected block.
document.addEventListener('shopify:block:select', (event) => {
  const block = event.target;
  const timeline = block.closest('ms-timeline');
  if (timeline) {
    const index = timeline.items.indexOf(block);
    if (index > -1) timeline.activate(index);
  }

  const slider = block.closest('ms-slider');
  if (slider && slider.scrollToItem) slider.scrollToItem(block);
});
